import { Box, MenuItem, TextField, Typography } from '@mui/material';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller } from 'react-hook-form';
import { US_STATES } from '../constants';
import { useDoctorForm } from '../hooks';
import { Doctor, DoctorFormData } from '../types';
import { mapDoctorToFormData } from '../utils';
import './DoctorForm.scss';

export interface DoctorFormHandles {
  submit: () => Promise<DoctorFormData | null>;
}

export interface DoctorFormProps {
  doctor: Doctor | null;
  insufficientEhr: boolean;
  defaultValues?: Partial<DoctorFormData>;
  hideNotes?: boolean;
}

const DoctorForm = forwardRef<DoctorFormHandles, DoctorFormProps>(
  ({ doctor, insufficientEhr, defaultValues, hideNotes }, ref) => {
    const { control, reset, trigger, getValues } = useDoctorForm();

    useEffect(() => {
      if (defaultValues) {
        reset({ ...defaultValues });
        return;
      }
      const base = doctor ? mapDoctorToFormData(doctor) : {};
      reset({
        ...base,
        additionalNotes: insufficientEhr
          ? 'pursue APS only (original EHR retrieved was insufficient)'
          : '',
      });
    }, [doctor, insufficientEhr, defaultValues, reset]);

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const isValid = await trigger();
        if (!isValid) return null;
        return getValues();
      },
    }));

    return (
      <Box className="form-grid">
        <Box className="form-field">
          <Typography component="label" className="form-label">
            Doctor&apos;s First Name
          </Typography>
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                fullWidth
                variant="outlined"
                placeholder="First name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box className="form-field">
          <Typography component="label" className="form-label">
            Doctor&apos;s Last Name
          </Typography>
          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                fullWidth
                variant="outlined"
                placeholder="Last name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box className="form-field full">
          <Typography component="label" className="form-label">
            Facility Name
          </Typography>
          <Controller
            control={control}
            name="facilityName"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                fullWidth
                variant="outlined"
                placeholder="Hospital, clinic or facility name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box className="form-field">
          <Typography component="label" className="form-label">
            Phone Number
          </Typography>
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                type="tel"
                fullWidth
                variant="outlined"
                placeholder="(555) 123-4567"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box className="form-field">
          <Typography component="label" className="form-label">
            ZIP Code
          </Typography>
          <Controller
            control={control}
            name="zipCode"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                fullWidth
                variant="outlined"
                placeholder="12345 or 12345-6789"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box className="form-field full">
          <Typography component="label" className="form-label">
            Street Address
          </Typography>
          <Controller
            control={control}
            name="streetAddress"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                fullWidth
                variant="outlined"
                placeholder="Street address"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box className="form-field">
          <Typography component="label" className="form-label">
            City
          </Typography>
          <Controller
            control={control}
            name="city"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                type="text"
                fullWidth
                variant="outlined"
                placeholder="City"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>

        <Box className="form-field">
          <Typography component="label" className="form-label">
            State
          </Typography>
          <Controller
            control={control}
            name="state"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                size="small"
                fullWidth
                variant="outlined"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              >
                <MenuItem value="">
                  <em>Select state…</em>
                </MenuItem>
                {US_STATES.map(state => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>

        {!hideNotes && (
          <Box className="form-field full">
            <Typography component="label" className="form-label">
              Additional Notes
            </Typography>
            <Controller
              control={control}
              name="additionalNotes"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  type="text"
                  fullWidth
                  variant="outlined"
                  placeholder="Any notes for this APS order…"
                  multiline
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Box>
        )}
      </Box>
    );
  },
);

DoctorForm.displayName = 'DoctorForm';

export default DoctorForm;
